import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { expenseService } from '../../services/expenseService';

const ViewExpensePage = () => {
  const { expenseId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();

  const [expense, setExpense] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isApproving, setIsApproving] = useState(false);

  useEffect(() => {
    fetchExpense();
  }, [expenseId, token]);

  const fetchExpense = async () => {
    if (!token || !expenseId) return;
    
    setIsLoading(true);
    try {
      const response = await expenseService.getExpenseById(expenseId, token);
      if (response.status === 'success') {
        setExpense(response.data.expense);
      } else {
        throw new Error(response.message || 'Failed to fetch expense');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch expense');
    } finally {
      setIsLoading(false);
    }
  };

  const handleApproveReject = async (status, notes = '') => {
    if (!['admin', 'manager'].includes(user?.role)) {
      setError('You do not have permission to approve/reject expenses');
      return;
    }

    setIsApproving(true);
    try {
      const response = await expenseService.approveExpense(expenseId, status, notes, token);
      if (response.status === 'success') {
        setExpense(response.data.expense);
      } else {
        throw new Error(response.message || 'Failed to update expense status');
      }
    } catch (err) {
      setError(err.message || 'Failed to update expense status');
    } finally {
      setIsApproving(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getCategoryLabel = (category) => {
    const categories = {
      'vehicle_expenses': 'Vehicle Expenses',
      'equipment_purchase': 'Equipment Purchase',
      'equipment_maintenance': 'Equipment Maintenance',
      'tools_supplies': 'Tools & Supplies',
      'office_supplies': 'Office Supplies',
      'software_subscriptions': 'Software Subscriptions',
      'insurance': 'Insurance',
      'licensing_permits': 'Licensing & Permits',
      'professional_services': 'Professional Services',
      'advertising_marketing': 'Advertising & Marketing',
      'training_education': 'Training & Education',
      'travel_meals': 'Travel & Meals',
      'utilities': 'Utilities',
      'rent_lease': 'Rent & Lease',
      'subcontractor_payments': 'Subcontractor Payments',
      'materials_inventory': 'Materials & Inventory',
      'bank_fees': 'Bank Fees',
      'interest_expense': 'Interest Expense',
      'depreciation': 'Depreciation',
      'other': 'Other'
    };
    return categories[category] || category;
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'approved': 'bg-green-100 text-green-800',
      'rejected': 'bg-red-100 text-red-800',
      'paid': 'bg-blue-100 text-blue-800',
      'reimbursed': 'bg-purple-100 text-purple-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading expense details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
          <div className="mt-4">
            <Link
              to="/admin/expenses"
              className="text-blue-600 hover:text-blue-800"
            >
              ← Back to Expenses
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!expense) {
    return (
      <div className="min-h-screen bg-gray-50 py-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Expense Not Found</h2>
            <p className="mt-2 text-gray-600">The expense you're looking for doesn't exist.</p>
            <Link
              to="/admin/expenses"
              className="mt-4 inline-block text-blue-600 hover:text-blue-800"
            >
              ← Back to Expenses
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <Link
            to="/admin/expenses"
            className="text-blue-600 hover:text-blue-800 mb-4 inline-block"
          >
            ← Back to Expenses
          </Link>
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{expense.title}</h1>
              <p className="text-gray-600 mt-1">Expense #{expense.expenseNumber}</p>
            </div>
            <div className="flex space-x-3">
              {user?.role === 'admin' || user?.role === 'manager' ? (
                <>
                  <Link
                    to={`/admin/expenses/${expenseId}/edit`}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Edit
                  </Link>
                  {expense.status === 'pending' && (
                    <>
                      <button
                        onClick={() => handleApproveReject('approved')}
                        disabled={isApproving}
                        className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => handleApproveReject('rejected')}
                        disabled={isApproving}
                        className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                      >
                        Reject
                      </button>
                    </>
                  )}
                </>
              ) : (
                expense.createdByUserId === user?.id && expense.status === 'pending' && (
                  <Link
                    to={`/admin/expenses/${expenseId}/edit`}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Edit
                  </Link>
                )
              )}
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        <div className="bg-white shadow-lg rounded-lg overflow-hidden">
          {/* Status Banner */}
          <div className={`px-6 py-4 ${getStatusColor(expense.status)}`}>
            <div className="flex justify-between items-center">
              <span className="font-medium capitalize">{expense.status}</span>
              <span className="text-sm">
                Last updated: {formatDate(expense.updatedAt)}
              </span>
            </div>
          </div>

          <div className="p-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Expense Details</h3>
                <dl className="space-y-3">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Amount</dt>
                    <dd className="text-lg font-semibold text-gray-900">{formatCurrency(expense.amount)}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Category</dt>
                    <dd className="text-sm text-gray-900">{getCategoryLabel(expense.category)}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Expense Date</dt>
                    <dd className="text-sm text-gray-900">{formatDate(expense.expenseDate)}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Payment Method</dt>
                    <dd className="text-sm text-gray-900 capitalize">{expense.paymentMethod.replace('_', ' ')}</dd>
                  </div>
                  {expense.vendor && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Vendor</dt>
                      <dd className="text-sm text-gray-900">{expense.vendor}</dd>
                    </div>
                  )}
                </dl>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Properties</h3>
                <dl className="space-y-3">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Tax Deductible</dt>
                    <dd className="text-sm text-gray-900">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        expense.isTaxDeductible ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {expense.isTaxDeductible ? 'Yes' : 'No'}
                      </span>
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Reimbursable</dt>
                    <dd className="text-sm text-gray-900">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        expense.isReimbursable ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {expense.isReimbursable ? 'Yes' : 'No'}
                      </span>
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Has Receipt</dt>
                    <dd className="text-sm text-gray-900">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        expense.hasReceipt ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {expense.hasReceipt ? 'Yes' : 'No'}
                      </span>
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Created By</dt>
                    <dd className="text-sm text-gray-900">
                      {expense.createdByUser ? 
                        `${expense.createdByUser.firstName} ${expense.createdByUser.lastName}` : 
                        'Unknown'
                      }
                    </dd>
                  </div>
                  {expense.approvedByUser && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Approved By</dt>
                      <dd className="text-sm text-gray-900">
                        {`${expense.approvedByUser.firstName} ${expense.approvedByUser.lastName}`}
                      </dd>
                    </div>
                  )}
                </dl>
              </div>
            </div>

            {/* Description */}
            {expense.description && (
              <div className="mb-8">
                <h3 className="text-lg font-medium text-gray-900 mb-3">Description</h3>
                <p className="text-gray-700 whitespace-pre-wrap">{expense.description}</p>
              </div>
            )}

            {/* Tags */}
            {expense.tags && expense.tags.length > 0 && (
              <div className="mb-8">
                <h3 className="text-lg font-medium text-gray-900 mb-3">Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {expense.tags.map(tag => (
                    <span
                      key={tag}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Documents */}
            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Documents</h3>
              <div className="space-y-3">
                {expense.receiptUrl && (
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                    <div className="flex items-center">
                      <svg className="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span className="ml-2 text-sm text-gray-900">Receipt</span>
                    </div>
                    <a
                      href={expense.receiptUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 text-sm"
                    >
                      View
                    </a>
                  </div>
                )}
                
                {expense.documentUrls && expense.documentUrls.length > 0 && (
                  <>
                    {expense.documentUrls.map((url, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                        <div className="flex items-center">
                          <svg className="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <span className="ml-2 text-sm text-gray-900">Document {index + 1}</span>
                        </div>
                        <a
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 text-sm"
                        >
                          View
                        </a>
                      </div>
                    ))}
                  </>
                )}

                {!expense.receiptUrl && (!expense.documentUrls || expense.documentUrls.length === 0) && (
                  <p className="text-gray-500 text-sm">No documents uploaded</p>
                )}
              </div>
            </div>

            {/* Notes */}
            {expense.notes && (
              <div className="mb-8">
                <h3 className="text-lg font-medium text-gray-900 mb-3">Notes</h3>
                <p className="text-gray-700 whitespace-pre-wrap">{expense.notes}</p>
              </div>
            )}

            {/* Timestamps */}
            <div className="pt-6 border-t border-gray-200">
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Created</dt>
                  <dd className="text-sm text-gray-900">{formatDate(expense.createdAt)}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Last Updated</dt>
                  <dd className="text-sm text-gray-900">{formatDate(expense.updatedAt)}</dd>
                </div>
                {expense.approvedAt && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Approved</dt>
                    <dd className="text-sm text-gray-900">{formatDate(expense.approvedAt)}</dd>
                  </div>
                )}
              </dl>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewExpensePage;
